import { useEffect, useRef, useState, useCallback } from "react";
import debounce from "lodash.debounce";
import { apiClient } from "../../../../api/httpClient";
import { EMAIL_DEBOUNCE_TIMEOUT } from "../../../../globals/utils/constants";
import { HISTORIC_GUARDIAN_BOND } from "../historicGuardianConfig";
import {
  fetchCityStateByZip,
  patchGuardianAddressRow,
} from "../../../../features/bonds/shared/guardianAddressHandlers";
import { formatSSN } from "../../../../components/guardian/principalHelpers";

/**
 * State and handlers for historic guardian forms (no JSX).
 */
export function useHistoricGuardianHandlers({
  bondKind,
  cfg,
  keys,
  data,
  updateData,
  globalData,
  role,
  pgId,
  professionalGuardians,
}) {
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const [emailExists, setEmailExists] = useState([]);
  const [emailChecking, setEmailChecking] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState([]);
  const emailDebounceRefs = useRef([]);

  const emailEnabled =
    cfg.emailUi === "newPgDebounced" || cfg.emailUi === "newPgOrAttorneyDebounced";

  useEffect(() => {
    if (!emailEnabled) return;
    setEmailExists((prev) => data.guardians.map((_, i) => prev[i] || false));
    setEmailChecking((prev) => data.guardians.map((_, i) => prev[i] || false));
    setEmailDialogOpen((prev) =>
      data.guardians.map((_, i) => prev[i] || false)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.guardians.length, emailEnabled]);

  const getDebouncedCheckEmail = useCallback(
    (idx) => {
      if (!emailDebounceRefs.current[idx]) {
        emailDebounceRefs.current[idx] = debounce(async (email) => {
          if (!email) {
            setEmailExists((prev) => {
              const arr = [...prev];
              arr[idx] = false;
              return arr;
            });
            setEmailChecking((prev) => {
              const arr = [...prev];
              arr[idx] = false;
              return arr;
            });
            setEmailDialogOpen((prev) => {
              const arr = [...prev];
              arr[idx] = false;
              return arr;
            });
            return;
          }
          setEmailChecking((prev) => {
            const arr = [...prev];
            arr[idx] = true;
            return arr;
          });
          try {
            await apiClient("/email-exists", "POST", { email });
            setEmailExists((prev) => {
              const arr = [...prev];
              arr[idx] = false;
              return arr;
            });
          } catch (e) {
            if (e?.detail === "Email already exists") {
              setEmailDialogOpen((prev) => {
                const arr = [...prev];
                arr[idx] = true;
                return arr;
              });
              setEmailExists((prev) => {
                const arr = [...prev];
                arr[idx] = true;
                return arr;
              });
            } else {
              setEmailExists((prev) => {
                const arr = [...prev];
                arr[idx] = false;
                return arr;
              });
            }
          } finally {
            setEmailChecking((prev) => {
              const arr = [...prev];
              arr[idx] = false;
              return arr;
            });
          }
        }, EMAIL_DEBOUNCE_TIMEOUT);
      }
      return emailDebounceRefs.current[idx];
    },
    []
  );

  const handleDialogClose = useCallback((idx) => {
    setEmailDialogOpen((prev) => {
      const arr = [...prev];
      arr[idx] = false;
      return arr;
    });
  }, []);

  const handleDialogContinue = useCallback((idx) => {
    setEmailDialogOpen((prev) => {
      const arr = [...prev];
      arr[idx] = false;
      return arr;
    });
    setEmailExists((prev) => {
      const arr = [...prev];
      arr[idx] = false;
      return arr;
    });
  }, []);

  const handleDialogCancel = useCallback(
    (idx) => {
      setEmailDialogOpen((prev) => {
        const arr = [...prev];
        arr[idx] = false;
        return arr;
      });
      const snap = dataRef.current;
      const updatedGuardians = [...snap.guardians];
      updatedGuardians[idx] = {
        ...updatedGuardians[idx],
        [keys.email]: "",
      };
      updateData({ ...snap, guardians: updatedGuardians });
      setEmailExists((prev) => {
        const arr = [...prev];
        arr[idx] = false;
        return arr;
      });
    },
    [keys.email, updateData]
  );

  const fetchProfessionalGuardianDetails = useCallback(
    async (index, id) => {
      try {
        const response = await apiClient(`/guardian-details/${id}/`, "GET");
        const currentData = dataRef.current;
        const updatedGuardians = Array.isArray(currentData.guardians)
          ? [...currentData.guardians]
          : [];
        updatedGuardians[index] = {
          ...updatedGuardians[index],
          [keys.name]: response.full_name,
          [keys.address]: response.mailing_address,
          [keys.city]: response.city,
          [keys.state]: response.state,
          [keys.zip]: response.zip_code,
          professional_guardian: id,
          is_professional_guardian: "Yes",
          is_new_pg: false,
          showFullForm: false,
        };
        updateData({ ...currentData, guardians: updatedGuardians });
      } catch (error) {
        console.error("Error fetching professional guardian details", error);
      }
    },
    [keys, updateData]
  );

  useEffect(() => {
    if (!role && pgId) {
      fetchProfessionalGuardianDetails(0, pgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, pgId, professionalGuardians]);

  const handleAttorneyHomeAddressChange = useCallback(
    (index, value, guardians, attorneys) => {
      const updatedGuardians = [...guardians];
      const guardian = updatedGuardians[index];
      const attorney = attorneys.find((a) => a.value === guardian.attorney);

      updatedGuardians[index] = {
        ...updatedGuardians[index],
        is_attorney_home: value,
      };

      if (value === "Yes") {
        updatedGuardians[index] = {
          ...updatedGuardians[index],
          [keys.address]: "",
          [keys.city]: "",
          [keys.state]: "",
          [keys.zip]: "",
        };
      } else if (attorney) {
        updatedGuardians[index] = {
          ...updatedGuardians[index],
          [keys.address]: attorney.address,
          [keys.city]: attorney.city,
          [keys.state]: attorney.state,
          [keys.zip]: attorney.zip_code,
        };
      }

      updateData({ ...dataRef.current, guardians: updatedGuardians });
    },
    [keys, updateData]
  );

  const handleAttorneySelection = useCallback(
    (index, attorney, guardians) => {
      if (bondKind === HISTORIC_GUARDIAN_BOND.va) {
        if (attorney) {
          const updatedGuardians = [...guardians];
          updatedGuardians[index] = {
            ...updatedGuardians[index],
            [keys.name]: attorney.label,
            [keys.address]: attorney.address,
            [keys.city]: attorney.city,
            [keys.state]: attorney.state,
            [keys.zip]: attorney.zip_code,
            is_attorney: "Yes",
            [keys.occupation]: attorney.position,
            [keys.relationship]: "Attorney of Record",
            [keys.citizenship]: "USA",
            [keys.country]: "USA",
            attorney: attorney.value,
            showAttorneyForm: false,
          };
          updateData({ ...dataRef.current, guardians: updatedGuardians });
        }
        return;
      }

      if (attorney) {
        const updatedGuardians = [...guardians];
        updatedGuardians[index] = {
          ...updatedGuardians[index],
          [keys.name]: attorney.label,
          [keys.address]: attorney.address,
          [keys.city]: attorney.city,
          [keys.state]: attorney.state,
          [keys.zip]: attorney.zip_code,
          is_attorney: "Yes",
          is_attorney_home: "No",
          [keys.occupation]: attorney.position,
          [keys.relationship]: "Attorney of Record",
          [keys.citizenship]: "USA",
          [keys.country]: "USA",
          attorney: attorney.value,
          showAttorneyForm: false,
        };
        updateData({ ...dataRef.current, guardians: updatedGuardians });
      } else {
        const updatedGuardians = [...guardians];
        updatedGuardians[index] = {
          ...updatedGuardians[index],
          is_attorney: "No",
          is_attorney_home: "No",
          attorney: null,
          [keys.name]: "",
          [keys.address]: "",
          [keys.city]: "",
          [keys.state]: "",
          [keys.zip]: "",
          [keys.occupation]: "",
          [keys.relationship]: "",
        };
        updateData({ ...dataRef.current, guardians: updatedGuardians });
      }
    },
    [bondKind, keys, updateData]
  );

  const handleGoogleCallback = useCallback(
    (index, address, city, state, zipCode) => {
      const currentData = dataRef.current;
      const guardians = patchGuardianAddressRow(
        currentData.guardians,
        index,
        keys,
        {
          [keys.address]: address,
          [keys.city]: city,
          [keys.state]: state,
          [keys.zip]: zipCode,
        }
      );
      updateData({ ...currentData, guardians });
    },
    [keys, updateData]
  );

  const handleZipCodeChange = useCallback(
    async (e, index) => {
      const zipCode = e.target.value;
      const currentData = dataRef.current;
      let guardians = patchGuardianAddressRow(currentData.guardians, index, keys, {
        [keys.zip]: zipCode,
      });
      updateData({ ...currentData, guardians });

      if (zipCode.length !== 5) return;

      try {
        const location = await fetchCityStateByZip(zipCode, cfg.zipLookup);
        if (!location) return;
        const freshData = dataRef.current;
        guardians = patchGuardianAddressRow(freshData.guardians, index, keys, {
          [keys.city]: location.city,
          [keys.state]: location.state,
          [keys.zip]: zipCode,
        });
        updateData({ ...freshData, guardians });
      } catch (error) {
        console.error("Error fetching city/state by zip:", error);
      }
    },
    [cfg.zipLookup, keys, updateData]
  );

  const updateGuardianAt = useCallback(
    (index, patch, guardians) => {
      updateData({
        ...dataRef.current,
        guardians: patchGuardianAddressRow(guardians, index, keys, patch),
      });
    },
    [keys, updateData]
  );

  const handleProfessionalGuardianChange = useCallback(
    (index, value, guardian, guardians) => {
      if (bondKind === HISTORIC_GUARDIAN_BOND.guardianship) {
        const updatedGuardians = [...guardians];
        updatedGuardians[index] = {
          ...updatedGuardians[index],
          is_professional_guardian: value,
          showFullForm: value === "Yes" ? guardian.showFullForm : false,
          professional_guardian: null,
          is_new_pg: false,
          attorney: null,
        };
        updateData({ ...dataRef.current, guardians: updatedGuardians });
        return;
      }

      if (bondKind === HISTORIC_GUARDIAN_BOND.probate) {
        const updatedGuardians = [...guardians];
        updatedGuardians[index] = {
          ...updatedGuardians[index],
          is_professional_guardian: value,
          professional_guardian: null,
          showFullForm: value === "Yes" ? guardian.showFullForm : false,
          is_new_pg: false,
          is_attorney: "No",
          attorney: null,
        };
        if (value === "No") {
          [
            keys.name,
            keys.address,
            keys.city,
            keys.state,
            keys.zip,
            keys.occupation,
            keys.relationship,
            keys.citizenship,
            keys.country,
            keys.ssn,
          ].forEach((field) => {
            delete updatedGuardians[index][field];
          });
        }
        updateData({ ...dataRef.current, guardians: updatedGuardians });
        return;
      }

      const updatedGuardians = [...guardians];
      updatedGuardians[index] = {
        ...updatedGuardians[index],
        is_professional_guardian: value,
        showFullForm: value === "Yes" ? guardian.showFullForm : false,
        professional_guardian: null,
        is_new_pg: false,
      };
      if (
        bondKind === HISTORIC_GUARDIAN_BOND.guardianship ||
        bondKind === HISTORIC_GUARDIAN_BOND.probate ||
        bondKind === HISTORIC_GUARDIAN_BOND.trustee
      ) {
        updatedGuardians[index].attorney = null;
      }
      updateData({ ...dataRef.current, guardians: updatedGuardians });
    },
    [bondKind, keys, updateData]
  );

  const handleAttorneyChange = useCallback(
    (index, value, guardians) => {
      if (bondKind === HISTORIC_GUARDIAN_BOND.guardianship) {
        const updatedGuardians = [...guardians];
        updatedGuardians[index] = {
          ...updatedGuardians[index],
          is_attorney: value,
          is_professional_guardian: "No",
          professional_guardian: null,
          attorney: null,
          showAttorneyForm: false,
          is_attorney_home: "No",
        };
        if (value === "No") {
          const fieldsToRemove = [
            "guardian_name",
            "guardian_street_address_1",
            "guardian_city",
            "guardian_state",
            "guardian_zip",
            "guardian_occupation",
            "guardian_relationship_to_ward",
            "guardian_citizenship",
            "country",
            "ssn",
            "is_attorney_home",
          ];
          fieldsToRemove.forEach((field) => {
            delete updatedGuardians[index][field];
          });
        }
        updateData({ ...dataRef.current, guardians: updatedGuardians });
        return;
      }

      if (bondKind === HISTORIC_GUARDIAN_BOND.probate) {
        const updatedGuardians = [...guardians];
        updatedGuardians[index] = {
          ...updatedGuardians[index],
          is_attorney: value,
          is_professional_guardian: "No",
          professional_guardian: null,
          attorney: null,
          showAttorneyForm: false,
          is_attorney_home: "No",
        };
        if (value === "No") {
          [
            keys.name,
            keys.address,
            keys.city,
            keys.state,
            keys.zip,
            keys.occupation,
            keys.relationship,
            keys.citizenship,
            keys.country,
            keys.ssn,
            "is_attorney_home",
          ].forEach((field) => {
            delete updatedGuardians[index][field];
          });
        }
        updateData({ ...dataRef.current, guardians: updatedGuardians });
        return;
      }

      updateGuardianAt(
        index,
        {
          is_attorney: value,
          attorney: null,
          showAttorneyForm: false,
        },
        guardians
      );
    },
    [bondKind, keys, updateData, updateGuardianAt]
  );

  const showHighBondSsn = useCallback(() => {
    const amt = globalData.basicInformation?.bondAmount;
    if (cfg.bondAmountSsnOp === "gte") return amt >= 250000;
    return amt > 250000;
  }, [cfg.bondAmountSsnOp, globalData.basicInformation?.bondAmount]);

  const showManualOccupationField = useCallback(
    (guardian) => {
      if (!cfg.showManualOccupation || cfg.manualOccupationWhen === "never") {
        return false;
      }
      if (cfg.manualOccupationWhen === "always") return true;
      const amt = globalData.basicInformation?.bondAmount;
      return (
        amt >= 250000 &&
        guardian.is_professional_guardian !== "Yes" &&
        guardian.is_attorney !== "Yes"
      );
    },
    [
      cfg.manualOccupationWhen,
      cfg.showManualOccupation,
      globalData.basicInformation?.bondAmount,
    ]
  );

  const showRelationshipField = useCallback(
    (guardian) => {
      if (cfg.relationshipShowWhen === "notPg") {
        return guardian.is_professional_guardian !== "Yes";
      }
      return (
        guardian.is_professional_guardian !== "Yes" &&
        guardian.is_attorney !== "Yes"
      );
    },
    [cfg.relationshipShowWhen]
  );

  return {
    emailExists,
    emailChecking,
    emailDialogOpen,
    getDebouncedCheckEmail,
    handleDialogClose,
    handleDialogContinue,
    handleDialogCancel,
    fetchProfessionalGuardianDetails,
    handleAttorneyHomeAddressChange,
    handleAttorneySelection,
    handleGoogleCallback,
    handleZipCodeChange,
    formatSSN,
    updateGuardianAt,
    handleProfessionalGuardianChange,
    handleAttorneyChange,
    showHighBondSsn,
    showManualOccupationField,
    showRelationshipField,
  };
}
